import styled, { css } from 'styled-components';
import { forwardRef } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { ROUTES } from '_constants/routes';

import * as Layout from '_components/atomic/layouts/form';
import Icon from '_components/atomic/Icon';
import Button from '_components/atomic/Button';
import BaseBannerSection from '_components/composed/sections/BaseBannerSection';
import FAQSection from '_components/composed/sections/FAQSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import ThreeStepSection from '_components/composed/sections/ThreeStepSection';
import WhySection from '_components/composed/sections/WhySection';
import React from 'react';
import RightPartWhyOfficeSection from '_components/composed/sections/RightPartWhyOfficeSection';
import { useSelector } from 'react-redux';
import { selectPriceMapByProcessingCode } from '_redux/variants/selector';
import { PROCESSING_OPTIONS_KEYS } from '_constants/processing';
import { PRODUCT_CODES } from '_constants/products';
import PriceSection from '_components/composed/sections/PriceSection';

const { publicRuntimeConfig } = getConfig();
const { colors, devices, spaces } = theme;

const StyledOfficeActionResponsePage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const OfficeActionResponsePage = () => {
  const router = useRouter();

  const priceMapByStandardProcessing = useSelector(
    selectPriceMapByProcessingCode(PROCESSING_OPTIONS_KEYS.standard)
  );

  const productPrice =
    priceMapByStandardProcessing[PRODUCT_CODES.tmOfficeActionResponse];
  return (
    <StyledOfficeActionResponsePage>
      <BaseBannerSection
        hideImageMobile
        imgSrc={`${publicRuntimeConfig.basePath}/images/office-action-response.svg`}
        title="Office Action Response"
        text="Don't give up on your trademark or miss the response deadline! Responses to the USPTO must be filed within 6 months of the mailing date printed on the Office Action document."
        buttons={[
          {
            onClick: () => router.push(ROUTES.officeActionResponseFiling),
            text: 'File Office Action Now',
            color: theme.colors.blue[500],
            suffix: (
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.white} />
            ),
          },
        ]}
      />
      <ThreeStepSection
        title="3 Step Process"
        steps={[
          {
            header: 'Complete Our Online Questionnaire',
            description:
              'All information gathered is confidential and secured.',
            icon: `${publicRuntimeConfig.basePath}/images/my-answers-bubble.svg`,
          },
          {
            header: 'Our Specialists Prepare Your Office Action Response',
            description:
              'Your Office Action issues are evaluated to verify eligibility for our handling. Once verified, the response is prepared and reviewed for submission.',
            icon: `${publicRuntimeConfig.basePath}/images/app-development-bubble.svg`,
          },
          {
            header: 'The Office Action Response is Filed with the UPSTO',
            description:
              'The response is reviewed with you prior to submission. Once approved and submitted, we will update you will all status changes.',
            icon: `${publicRuntimeConfig.basePath}/images/app-filed.svg`,
          },
        ]}
      />
      <Layout.Divider desktop size={spaces.large24X} />
      <WhySection
        title="Determining Issues to Be Addressed in Response"
        leftImage={`${publicRuntimeConfig.basePath}/images/office-action-response-why.png`}
        rightPart={<RightPartWhyOfficeSection />}
      />
      <PriceSection
        title="Office Action Response"
        subtitle="WE’LL HELP YOU OVERCOME YOUR OFFICE ACTION."
        price={productPrice}
        priceText="per filing"
        bullets={[
          'Assessment of response eligibility',
          'Professionally formatted report and review done by specialists',
          'High approval rate from our responses',
        ]}
        buttons={[
          {
            text: 'File Office Action Now',
            color: colors.white,
            textColor: colors.blue[500],
            suffix: (
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.blue[500]} />
            ),
            onClick: () => router.push(ROUTES.officeActionResponseFiling),
          },
        ]}
      />
      <FAQSection />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => router.push(ROUTES.trademarkRegistrationFiling)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            File Office Action Now
          </Button>
        }
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle
        size={686}
        rotate={90}
        right={-135}
        top={'calc(28% + 10px)'}
      />
      <StyledCircle
        size={743}
        rotate={180}
        left={-197}
        top={'calc(32% + 30px)'}
      />
      <StyledImageContainer left={65} top={'50%'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle
        size={820}
        rotate={-180}
        right={-202}
        top={'calc(73% + 10px)'}
      />
      <StyledImageContainer right={65} top={'calc(80% + 20px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledOfficeActionResponsePage>
  );
};

export default OfficeActionResponsePage;
